import { PiGlobeHemisphereWestBold, PiHouseBold } from 'react-icons/pi';

const VALID_SCOPES = ['global', 'organization'];

// Note: this function is derived from the API and meant
// to be kept in sync. It is slightly modified to not use
// mongoose utility methods.
export function userHasAccess(user, options) {
  if (!user) {
    return false;
  }

  const { scope = 'global', permission = 'read', endpoint, scopeRef } = options;

  if (!endpoint) {
    throw new Error('Expected endpoint (e.g. users)');
  } else if (!permission) {
    throw new Error('Expected permission (e.g. read)');
  } else if (!scope) {
    throw new Error('Expected scope (e.g. organization)');
  } else if (!VALID_SCOPES.includes(scope)) {
    throw new Error('Invalid scope');
  }

  return user.roles.some((r) => {
    if (scope === 'global' && r.scope !== 'global') {
      return false;
    } else if (scope === 'organization' && r.scope === 'organization') {
      if (r.scopeRef !== scopeRef) {
        return false;
      }
    }

    const definition = r.roleDefinition;
    const allowed = definition?.permissions?.[endpoint];

    if (!definition) {
      throw new Error(`Unknown role "${r.role}".`);
    }

    if (Array.isArray(allowed)) {
      return allowed.includes(permission);
    } else if (allowed === permission || allowed === 'all') {
      return true;
    } else {
      return false;
    }
  });
}

// Owner-only controls, of which there are deliberately few: the org
// sharing kill-switch and erasure. Both are gated server-side on
// `organizations.write`, which only `owner` holds.
//
// Ask this rather than "is an admin", and ask it wherever a control
// exists that the API will refuse. An org admin reaching a button that
// 403s is the exact shape of the bug this helper was added for — the
// console offered Activity log and CLI to admins while roles.json
// refused them, for weeks, because the nav and the API were answering
// different questions.
export function isOrgOwner(user, organization) {
  if (!user || !organization) {
    return false;
  }
  return (user.roles || []).some((role) => {
    if (role.scope === 'global' && ['admin', 'superAdmin'].includes(role.role)) {
      return true;
    }
    return (
      role.scope === 'organization' &&
      String(role.scopeRef) === String(organization.id) &&
      role.role === 'owner'
    );
  });
}

export function userCanSwitchOrganizations(user) {
  if (!user) {
    return false;
  }
  const nonGlobalRoles = user.roles.filter((role) => {
    return role.scope !== 'organization';
  });
  if (nonGlobalRoles.length > 1) {
    return true;
  } else {
    return userHasAccess(user, {
      endpoint: 'organizations',
      permission: 'read',
      scope: 'global',
    });
  }
}

export function formatRoles(roles) {
  const labels = [];
  const scopeRefs = [];
  roles.forEach((role) => {
    if (role.scope === 'global') {
      labels.push({
        key: `global-${role.role}`,
        content: role.roleDefinition.name,
        icon: PiGlobeHemisphereWestBold,
      });
    } else {
      const key = `${role.role}-${role.scope}-${role.scopeRef}`;
      if (!scopeRefs.includes(key)) {
        scopeRefs.push(key);
        labels.push({
          key: key,
          content: role.roleDefinition.name,
          icon: PiHouseBold,
        });
      }
    }
  });
  return labels;
}
